'use client';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import FixedHeader from '../MUI/HeaderFixedLayout';
import { BackIcon, CheckedRadioButton, CloseIcon, UnCheckedRadioButton } from '@/public/icon';
import { writingService } from '@/app/service/writingService';
import CenteredBox from '@/app/base-ui/CenterBox';
import { CreatePostRequest, EditPostRequest } from '@/app/models/writing/WritingPosts';
import ChooseGroup from './ChooseGroup';
import DrawerCustom from '../MUI/CustomDrawer';
import BaseModal from '@/app/base-ui/BaseModalUI';
import LevelUpModal from '@/app/components/Common/LevelUpModal';
import { DraftMedia, MediaOperation, UpdateMedia } from '@/app/models/media/IMedia';
import { useSnackbar } from '@/app/contexts/snack-bar-provider';
import Writing from './Writing';
import { AbsoluteValue, FormEditMode, WritingStep } from '@/app/constants/typedef';
import { IGroup } from '@/app/models/group/IGroup';
import { useRouter } from 'next/navigation';
import { useGlobalDialog } from '@/app/contexts/global-dialog';
import utilsService from '@/app/helpers/utilsService';
import { usePostContext } from '@/app/contexts/feeds-context';
import { PostModel } from '@/app/models/posts/Posts';
import { FileAttach } from '@/app/models/file/fileModel';
import UrlFormWriting from './UrlFormWriting';
import { useUser } from '@/app/hooks/useUser';
import NewWritingForm from './NewWritingForm';
import { useTranslation } from '@/app/hooks/useTranslation';

interface WritingComponentProps {
    data?: EditPostRequest;
    groups?: IGroup[];
    loadingGroups?: boolean;
    id?: string;
    updatePostDetail?: (postDetailData: PostModel) => void;
    onClose?: () => void;
}

type RadioTypePost = 'image' | 'URL';

const WritingComponent: React.FC<WritingComponentProps> = ({ data, groups, loadingGroups, id, updatePostDetail, onClose }) => {
    const router = useRouter();
    const { user } = useUser();
    const { t } = useTranslation();
    const [done, setDone] = useState<boolean>(false); // Only used to disable button during submission
    const editMode = useRef<FormEditMode>(id ? FormEditMode.Edit : FormEditMode.Create);
    const existingData = useRef({ media: data?.media });
    // Only set Url if the media type is 'url', not 'image' or 'video'
    const Url = data?.media?.length && 'mediaUrl' in data?.media?.[0] && data.media[0].mediaType === 'url' ? (data.media[0].mediaUrl as string | null) : (null as string | null);
    const { alertDialog, editPostDialog, loadingDialog, isDialogOpen, isDialogEditPostOpen } = useGlobalDialog();
    const { addSnackbar } = useSnackbar();
    const { addPost, updatePost } = usePostContext();
    const captions = useRef(data?.caption);
    const isPublic = useRef(data?.isPublic);
    const groupId = useRef(data?.groupId);
    const [group, setGroup] = useState<IGroup[]>(groups ?? []);
    const [openDrawer, setOpenDrawer] = useState<boolean>(false);
    const [radioValue, setRadioValue] = useState<RadioTypePost>(data?.media?.length && 'mediaType' in data?.media?.[0] && data?.media?.[0].mediaType === 'url' ? 'URL' : 'image');
    const [showAlert, setShowAlertPopup] = useState<boolean>(false);
    const [showAlertForSubmitAction, setShowAlertForSubmitAction] = useState(false);
    const [isGroupSelected, setIsGroupSelected] = useState<string | number>(() => {
        return data?.groupId || 'all';
    });
    const [step, setStep] = useState<WritingStep | null>(null);
    const [fileAttachment, setFileAttachment] = useState<FileAttach | null>(null);
    const [loadingSubmit, setLoadingSubmit] = useState<boolean>(false);
    const [modalContent, setModalContent] = useState({ title: '', description: '' });
    const [url, setUrl] = useState<string | null>(Url);
    const [isWrongUrl, setIsWrongUrl] = useState<boolean>(false);
    const [bonusPoints, setBonusPoints] = useState<number>(0);
    const [showBonusModal, setShowBonusModal] = useState<boolean>(false);
    const [levelUpData, setLevelUpData] = useState<any>(null);
    const [showLevelUpModal, setShowLevelUpModal] = useState<boolean>(false);
    const [pendingNavigation, setPendingNavigation] = useState<{ route: string } | null>(null);
    const urlFormRef = useRef<{ handleCheckUrl: () => void }>(null);
    const newWritingFormRef = useRef<{ handleSubmit: () => void; canSubmit: () => boolean }>(null);
    const [state, setState] = useState<CreatePostRequest | EditPostRequest>(() => {
        if (data) {
            return {
                caption: data.caption || '',
                media: data.media || [],
                groupId: data.groupId || 'all',
                isPublic: data.isPublic !== undefined ? data.isPublic : true,
            };
        }
        return {
            caption: '',
            media: [],
            groupId: 'all',
            isPublic: true,
        };
    });
    const [tempTags, setTempTags] = useState<string[]>(() => {
        return data?.tags || [];
    });
    const [tempBoardType, setTempBoardType] = useState<'market' | 'board'>(() => {
        return data?.boardType || 'board';
    });
    const [tempPrice, setTempPrice] = useState<string>(() => {
        return data?.price || '';
    });
    const [tempProductName, setTempProductName] = useState<string>(() => {
        return data?.productName || '';
    });

    // REMOVED: useEffect that syncs with data prop
    // This was causing re-renders after save because updatePostDetail changes data prop
    // Solution: Component owns its state, no need to sync back from prop after initial load

    useEffect(() => {
        // Only reset when using global dialogs (not when using onClose prop from EditPostModal)
        if (!onClose) {
            if (!isDialogOpen && !id) {
                resetState();
            }
            if (!isDialogEditPostOpen && id) {
                resetState();
            }
        }
    }, [isDialogOpen, isDialogEditPostOpen, onClose]);

    useEffect(() => {
        if (!loadingGroups) {
            setGroup(groups ?? []);
            // Always use NEW_WRITING_FORM for both create and edit
            setStep(WritingStep.NEW_WRITING_FORM);
        }
    }, [groups, id, loadingGroups]);

    const resetState = () => {
        setGroup([]);
        setOpenDrawer(false);
        setRadioValue('image');
        setFileAttachment(null);
        setState({
            caption: '',
            media: [],
            groupId: 'all',
            isPublic: false,
        });
        setShowAlertPopup(false);
        setIsGroupSelected('all');
        setLoadingSubmit(false);
        setStep(null);
        setDone(false);
        setModalContent({ title: '', description: '' });
        setUrl(null);
        setIsWrongUrl(false);
    };

    const handleBackToChooseGroup = useCallback(() => {
        setStep(WritingStep.CHOOSE_GROUP_WITHOUT_SELECT_IMG);
    }, []);

    const handleBackToUrlScreen = useCallback(() => {
        setStep(WritingStep.WRITING_URL);
    }, []);

    const handleChangeFileAttach = (dataFile: FileAttach | null) => {
        setFileAttachment(dataFile);
    };

    const handlePopup = useCallback(
        (step: WritingStep, action: string) => {
            if ([WritingStep.NEW_WRITING_FORM, WritingStep.WRITING_POST, WritingStep.WRITING_URL, WritingStep.CHOOSE_GROUP_WITHOUT_SELECT_IMG].includes(step)) {
                if (action === 'cancel_writing') {
                    setState((prev) => ({ ...prev, caption: '', media: [], isPublic: false }));
                    setShowAlertPopup(false);
                    // Use onClose callback if provided (for EditPostModal)
                    if (onClose) {
                        onClose();
                    } else {
                        if (id) editPostDialog.close();
                        else alertDialog.close();
                    }
                } else {
                    setShowAlertPopup(false);
                }
            } else {
                setShowAlertPopup(false);
            }
        },
        [id, onClose]
    );

    const handleChangeTypeRadio = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        if (['3D'].includes(value)) {
            setModalContent({
                title: t('feature_under_development', '이 기능은 열심히 개발중입니다.<br />멋진 업데이트로 찾아뵙겠습니다!'),
                description: '',
            });
            setShowAlertPopup(true);
            setOpenDrawer(false);
            return;
        }

        // Clear URL state when switching to image type
        if (value === 'image' && radioValue === 'URL') {
            setUrl(null);
            setIsWrongUrl(false);
        }

        setRadioValue(value as RadioTypePost);
        setOpenDrawer(false);
    }, [radioValue]);

    const handleConfirmClose = useCallback(() => {
        if ([WritingStep.NEW_WRITING_FORM].includes(step as WritingStep)) {
            // In edit mode, check if data has changed
            if (id) {
                const hasBasicChanges = state.caption !== captions.current ||
                    state.groupId !== groupId.current ||
                    state.isPublic !== isPublic.current ||
                    JSON.stringify(state.media) !== JSON.stringify(existingData.current.media);

                const hasProductChanges = tempProductName !== (data?.productName || '') ||
                    tempPrice !== (data?.price || '') ||
                    JSON.stringify(tempTags) !== JSON.stringify(data?.tags || []) ||
                    tempBoardType !== (data?.boardType || 'board');

                if (hasBasicChanges || hasProductChanges) {
                    setModalContent({
                        title: t('changes_lost', '변경된 사항이 모두 사라집니다.<br />수정하기 페이지를 나가겠습니까?'),
                        description: '',
                    });
                    setShowAlertPopup(true);
                    return;
                }
            } else {
                // In create mode, check if any content exists
                if (state.caption || state.media.length > AbsoluteValue.Number || fileAttachment?.fileUrl) {
                    setModalContent({
                        title: t('changes_lost', '변경된 사항이 모두 사라집니다.<br />수정하기 페이지를 나가겠습니까?'),
                        description: '',
                    });
                    setShowAlertPopup(true);
                    return;
                }
            }
        }
        if ([WritingStep.CHOOSE_GROUP_WITHOUT_SELECT_IMG].includes(step as WritingStep)) {
            setStep(WritingStep.WRITING_POST);
            return;
        }
        if ([WritingStep.WRITING_URL].includes(step as WritingStep)) {
            // Clear URL state when closing modal without attaching
            // User pressed X without pressing 첨부 button
            setUrl(null);
            setIsWrongUrl(false);
            // Switch back to image tab
            setRadioValue('image');
            setStep(WritingStep.NEW_WRITING_FORM);
            return;
        }
        // Use onClose callback if provided (for EditPostModal)
        if (onClose) {
            onClose();
        } else {
            // Otherwise use global dialog system (for /writing page)
            if (id) editPostDialog.close();
            else alertDialog.close();
        }
    }, [step, state, id, fileAttachment, onClose]);

    const handleSetSelectGroup = useCallback(
        (key: string | number) => {
            //Cao Thien closed in 29/06/2024
            if (step === WritingStep.CHOOSE_GROUP_WITHOUT_SELECT_IMG) {
                setIsGroupSelected(key);
                setState((prev) => ({ ...prev, groupId: key as string }));
                // setOpenDrawer(true);

                setStep(WritingStep.WRITING_POST);
                return;
            }
            // closed it for bug stage 1
            setIsGroupSelected(key);
            setState((prev) => ({ ...prev, groupId: key as string }));
            setOpenDrawer(true);
            setIsGroupSelected(key);
        },
        [step]
    );

    const handleRemoveImage = useCallback(async (mediaToRemove: MediaOperation) => {
        setState((prev: any) => {
            if ('mediaId' in mediaToRemove) {
                // For newly added media with unique mediaId
                if ('draftUrl' in mediaToRemove) {
                    URL.revokeObjectURL(mediaToRemove.draftUrl);
                }
                return {
                    ...prev,
                    media: prev.media.filter((item: any) => !('mediaId' in item && item.mediaId === mediaToRemove.mediaId)),
                };
            } else if ('draftUrl' in mediaToRemove) {
                // Fallback for legacy media without mediaId
                URL.revokeObjectURL(mediaToRemove.draftUrl);
                return {
                    ...prev,
                    media: prev.media.filter((item: any) => !('draftUrl' in item && item.draftUrl === mediaToRemove.draftUrl)),
                };
            } else if ('id' in mediaToRemove) {
                // For uploaded media with server id
                return {
                    ...prev,
                    media: prev.media.map((item: any) => ('id' in item && item.id === mediaToRemove.id ? { id: item.id, isDeleted: true } : item)),
                };
            }
            return prev;
        });
    }, []);

    const onChangeCaption = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prev) => ({ ...prev, caption: event.target.value }));
    }, []);

    const onUpdateUrlAndStatusUrl = (url: string | null, isWrongUrl: boolean) => {
        setUrl(url);
        setIsWrongUrl(isWrongUrl);
    };

    const updateUrl = () => {
        const newMediaUrl = {
            mediaType: 'url',
            mediaUrl: url,
            order: 1,
            url: url ?? '',
        };

        if (id) {
            if (!utilsService.hasKey(state.media[0], 'isDeleted')) {
                // No media has been deleted -> overwrite the first media's url and mediaUrl
                setState((prev: any) => ({
                    ...prev,
                    media: [{ ...state.media[0], url: url, mediaUrl: url }],
                }));
            } else {
                // Media has been deleted -> keep previous deleted and add new media
                setState((prev: any) => {
                    const deletedMedia = prev.media?.filter((item: any) => item.isDeleted);
                    const updatedMedia = [...deletedMedia, newMediaUrl].sort((a, b) => (a.order || 0) - (b.order || 0));
                    return { ...prev, media: updatedMedia };
                });
            }
        } else {
            // id doesn't exist -> create a new media with order 1
            setState((prev: any) => {
                const updatedMedia = [newMediaUrl].sort((a, b) => (a.order || 0) - (b.order || 0));
                return { ...prev, media: updatedMedia };
            });
        }
    };

    const handleDeleteUrl = (item: MediaOperation) => {
        setState((prev: any) => {
            if (!('id' in item)) {
                // Filter out URL media items completely
                return {
                    ...prev,
                    media: prev.media.filter((m: any) => !('url' in m)),
                };
            } else {
                return {
                    ...prev,
                    media: prev.media.map((m: any) => ('id' in m && m.id === item.id ? { id: m.id, isDeleted: true } : m)),
                };
            }
        });
        setUrl(null);
        setIsWrongUrl(false);
        setRadioValue('image');
        // Don't navigate away - stay on current form
    };

    const handleAvatarChange = useCallback(
        async (selectedImages: MediaOperation[]) => {
            // Check file size IMMEDIATELY (10MB = 10 * 1024 * 1024 bytes)
            const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
            const oversizedFiles = selectedImages.filter(img => {
                if ('file' in img && img.file instanceof File) {
                    return img.file.size > MAX_FILE_SIZE;
                }
                return false;
            });

            if (oversizedFiles.length > 0) {
                const fileNames = oversizedFiles
                    .map(f => ('file' in f && f.file instanceof File) ? f.file.name : '')
                    .filter(Boolean)
                    .join(', ');

                addSnackbar({
                    message: t('file_too_large', `이미지는 최대 10MB까지 업로드할 수 있습니다.`),
                    severity: 'error',
                    autoHideDuration: 5000,
                    vertical: 'bottom',
                    horizontal: 'center',
                    type: 'toast',
                });
                return;
            }

            // Check total count first
            if ([...selectedImages, ...state.media].length > 10) {
                addSnackbar({
                    message: t('max_media_count', '최대 10장까지 업로드할 수 있습니다.'),
                    severity: 'error',
                    autoHideDuration: 3000,
                    vertical: 'bottom',
                    horizontal: 'center',
                    type: 'toast',
                });
                return;
            }

            const newImages: MediaOperation[] = selectedImages.map((file, index) => {
                if (file && 'file' in file && file.file instanceof File) {
                    const previewUrl: string = 'preview' in file && file.preview
                        ? String(file.preview)
                        : URL.createObjectURL(file.file);
                    return {
                        mediaId: `draft_${Date.now()}_${index}_${Math.random()}`,
                        mediaType: file.file.type.startsWith('image/') ? 'image' : 'video',
                        mediaUrl: previewUrl,
                        draftUrl: previewUrl,
                        order: state.media.length + index + 1,
                        file: file.file,
                    } as DraftMedia;
                }
                return file;
            });

            setState((prev) => {
                const updatedPhotos = [...prev.media, ...newImages] as UpdateMedia[];
                updatedPhotos.sort((a, b) => (a.order || 0) - (b.order || 0));
                return { ...prev, media: updatedPhotos };
            });

            setOpenDrawer(false);
            // Chỉ chuyển sang WRITING_POST nếu không phải NEW_WRITING_FORM
            if (step !== WritingStep.NEW_WRITING_FORM) {
                setStep(WritingStep.WRITING_POST);
            }
        },
        [state.media, step]
    );

    const handleWritingWithoutImg = () => {
        setOpenDrawer(false);
        setStep(WritingStep.WRITING_POST);
    };

    const isDirty = () => {
        const hasBasicChanges =
            state.caption !== captions.current ||
            state.groupId !== groupId.current ||
            state.isPublic !== isPublic.current ||
            JSON.stringify(state.media) !== JSON.stringify(existingData.current.media);

        const hasProductChanges =
            tempProductName !== (data?.productName || '') ||
            tempPrice !== (data?.price || '') ||
            JSON.stringify(tempTags) !== JSON.stringify(data?.tags || []) ||
            tempBoardType !== (data?.boardType || 'board');

        return hasBasicChanges || hasProductChanges;
    };

    const reorderMedia = (mediaList: any[]) => {
        const deletedItems = mediaList.filter((m: any) => 'isDeleted' in m && m.isDeleted);
        const activeItems = mediaList
            .filter((m: any) => !('isDeleted' in m && m.isDeleted))
            .map((media: any, index: number) => ({
                ...media,
                order: index + 1
            }));
        return [...activeItems, ...deletedItems];
    };

    const handleWritingPost = async () => {
        try {
            if (loadingSubmit) return;
            setLoadingSubmit(true);
            loadingDialog.open();

            // Validate caption (required)
            if (!state.caption || state.caption.trim().length < 1) {
                setModalContent({
                    title: t('caption_required', '캡션을 입력해주세요.'),
                    description: '',
                });
                setShowAlertForSubmitAction(true);
                setLoadingSubmit(false);
                loadingDialog.close();
                return;
            }

            // Validate tags (required)
            if (!tempTags || tempTags.length < 1) {
                setModalContent({
                    title: t('tags_required', '최소 1개의 태그를 입력해주세요.'),
                    description: '',
                });
                setShowAlertForSubmitAction(true);
                setLoadingSubmit(false);
                loadingDialog.close();
                return;
            }

            // Validate media (required) - check for images/videos/url
            if (!state.media || state.media.length < 1) {
                setModalContent({
                    title: t('media_required', '이미지 또는 동영상을 추가해주세요.'),
                    description: '',
                });
                setShowAlertForSubmitAction(true);
                setLoadingSubmit(false);
                loadingDialog.close();
                return;
            }
            if (editMode.current == FormEditMode.Edit) {
                if (!isDirty()) {
                    loadingDialog.close();
                    addSnackbar({
                        message: t('post_updated', '게시물을 업데이트했어요.'),
                        severity: 'info',
                        autoHideDuration: 3000,
                        vertical: 'bottom',
                        horizontal: 'center',
                        type: 'toast',
                    });

                    // Close modal using onClose callback or global dialog
                    if (onClose) {
                        onClose();
                    } else {
                        editPostDialog.close();
                    }
                    return;
                }

                const reorderedStateMedia = reorderMedia(state.media);
                const checkingChangedPhotos = utilsService.filterChangedData({ media: reorderedStateMedia }, existingData.current);
                const dataEdit: EditPostRequest | any = {
                    caption: state.caption || null,
                    media: [],
                    isPublic: 1,
                    // groupId: state.groupId == 'all' ? null : state.groupId,
                };
                if (state.groupId) {
                    if (state.groupId !== 'all') {
                        dataEdit['groupId'] = state.groupId;
                    }
                }
                // Handle media changes based on current mediaType (radioValue)
                if (radioValue === 'URL' && url) {
                    // When switching to URL, delete old images/videos and add new URL
                    const deletedMedia = existingData.current.media
                        ?.filter((m: any) => m.id && m.mediaType !== 'url')
                        .map((m: any) => ({ id: m.id, isDeleted: 1 })) || [];

                    const urlMedia = {
                        mediaType: 'url',
                        url: url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`,
                        order: 1,
                    };

                    dataEdit['media'] = [...deletedMedia, urlMedia];
                } else if (radioValue === 'image') {
                    // When switching to image/video, delete old URLs
                    const deletedUrls = existingData.current.media
                        ?.filter((m: any) => m.id && m.mediaType === 'url')
                        .map((m: any) => ({ id: m.id, isDeleted: 1 })) || [];

                    // Add new/changed images/videos
                    let newMedia: any[] = [];
                    if (Object.keys(checkingChangedPhotos)?.length > 0) {
                        newMedia = checkingChangedPhotos?.media
                            ?.map((media: any) => {
                                if (media.isDeleted) {
                                    return { isDeleted: media.isDeleted ? 1 : 0, id: media.id };
                                }

                                // For existing media (has id) without file, don't send them
                                // They haven't changed and shouldn't be re-uploaded
                                if (media.id && !media.file) {
                                    return null;
                                }

                                // Only send new media (has file) or changed media
                                // Don't send mediaUrl - it's just a base64 preview
                                return {
                                    mediaType: media.mediaType,
                                    [media.mediaType === 'video' ? 'video' : 'image']: media.file,
                                    order: media.order,
                                };
                            })
                            .filter(Boolean); // Remove null entries
                    }

                    dataEdit['media'] = [...deletedUrls, ...newMedia];
                }

                // Add product-specific fields - always send to allow updates/deletions
                dataEdit['tags'] = tempTags || [];
                dataEdit['boardType'] = tempBoardType;

                // Add post_type based on boardType
                const postType = tempBoardType === 'market' ? 'product' : 'post';
                dataEdit['post_type'] = postType;

                if (tempBoardType === 'market') {
                    dataEdit['price'] = tempPrice || '';
                    dataEdit['name'] = tempProductName || '';
                } else {
                    // When switching from market to board, clear product fields
                    dataEdit['price'] = null;
                    dataEdit['name'] = null;
                }

                const { data: responseData } = await writingService.updateFeedForOnlyPosts({
                    id: id as string,
                    type: 'posts',
                    data: dataEdit,
                });

                updatePost(new PostModel(responseData.data));
                updatePostDetail?.(new PostModel(responseData.data));

                // Check for bonus points first
                if (responseData?.data?.bonus_point && responseData.data.bonus_point > 0) {
                    setBonusPoints(responseData.data.bonus_point);
                    setShowBonusModal(true);

                    // If also has level up, set it up but don't show yet (will show after bonus modal closes)
                    if (responseData?.data?.level_up && responseData.data.level_up.level_new && responseData.data.level_up.benefit) {
                        setLevelUpData(responseData.data.level_up);
                    }
                }
                // Check for level up if no bonus points
                else if (responseData?.data?.level_up && responseData.data.level_up.level_new && responseData.data.level_up.benefit) {
                    setLevelUpData(responseData.data.level_up);
                    setShowLevelUpModal(true);
                } else {
                    addSnackbar({
                        message: t('post_updated', '게시물을 업데이트했어요.'),
                        severity: 'info',
                        autoHideDuration: 3000,
                        vertical: 'bottom',
                        horizontal: 'center',
                        type: 'toast',
                    });
                }
                // Don't close here - let it fall through to common close logic below
            } else {
                let parseFieldDataMedia: any = [];
                if (radioValue === 'image') {
                    // Reorder media to prevent duplicate order values
                    const reorderedMedia = reorderMedia(state.media);
                    parseFieldDataMedia = reorderedMedia.map((media: any) => {
                        // Don't send mediaUrl if we have a file - it's just a base64 preview
                        // Backend will generate the actual URL after upload
                        return {
                            mediaType: media.mediaType,
                            [media.mediaType === 'video' ? 'video' : 'image']: media.file, // Dynamically set based on media type
                            order: media.order,
                        };
                    });
                }
                if (url && radioValue == 'URL') {
                    // Add https:// prefix if URL doesn't already have a protocol
                    const normalizedUrl = url.startsWith('http://') || url.startsWith('https://') ? url : `https://${url}`;
                    const mediaUrlType = {
                        mediaType: 'url',
                        url: normalizedUrl,
                        order: parseFieldDataMedia.length + 1,
                    };
                    parseFieldDataMedia = [...parseFieldDataMedia, mediaUrlType];
                }
                const isLeader = group.find((group: any) => group.id == state.groupId)?.role.id == 1;
                if (fileAttachment?.fileUrl && (isLeader || user?.isExpert || user?.isInfluencer)) {
                    const filePDF = {
                        mediaType: 'file',
                        mediaUrl: fileAttachment?.fileUrl,
                        file: fileAttachment?.file,
                        fileName: fileAttachment.base,
                        order: parseFieldDataMedia.length + 1,
                    };
                    parseFieldDataMedia = [...parseFieldDataMedia, filePDF];
                }

                const dataPost: CreatePostRequest | any = {
                    caption: state.caption,
                    media: parseFieldDataMedia,
                    isPublic: 1, // default is true
                };
                if (state.groupId !== 'all') dataPost['groupId'] = state.groupId;

                // Add tags, boardType, price, productName if they exist (from NewWritingForm)
                if (tempTags && tempTags.length > 0) {
                    dataPost['tags'] = tempTags;
                }
                if (tempBoardType) {
                    dataPost['boardType'] = tempBoardType;
                }
                if (tempBoardType === 'market' && tempPrice) {
                    dataPost['price'] = tempPrice;
                }
                if (tempBoardType === 'market' && tempProductName) {
                    dataPost['name'] = tempProductName;
                }

                // Add post_type field based on boardType
                const postType = tempBoardType === 'market' ? 'product' : 'post';
                dataPost['post_type'] = postType;

                const dataFinal = utilsService.cleanObject(dataPost);
                const response = await writingService.writingPost({ ...dataFinal, isPublic: 1 } as CreatePostRequest);

                // Safety check for response structure
                if (!response || !response.data) {
                    console.error('Invalid API response:', response);
                    throw new Error('서버에서 잘못된 응답을 받았습니다');
                }

                const { data } = response;
                addPost(new PostModel(data.data));

                // Calculate route for navigation
                const route = postType === 'product'
                    ? `/community/products/${data.data.id}`
                    : `/community/posts/${data.data.id}`;

                // Check for bonus points first
                if (data?.data?.bonus_point && data.data.bonus_point > 0) {
                    setBonusPoints(data.data.bonus_point);
                    setTimeout(() => {
                        setShowBonusModal(true);
                    }, 0);

                    if (data?.data?.level_up && data.data.level_up.level_new && data.data.level_up.benefit) {
                        setLevelUpData(data.data.level_up);
                    }

                    // Store route for navigation after modal closes
                    setPendingNavigation({ route });

                    // Close loading dialog but don't close other dialogs yet (wait for modal to close)
                    loadingDialog.close();
                    return; // Exit early to prevent running common close logic
                }
                // Check for level up if no bonus points
                else if (data?.data?.level_up && data.data.level_up.level_new && data.data.level_up.benefit) {
                    setLevelUpData(data.data.level_up);
                    setTimeout(() => {
                        setShowLevelUpModal(true);
                    }, 0);

                    // Store route for navigation after modal closes
                    setPendingNavigation({ route });

                    // Close loading dialog but don't close other dialogs yet (wait for modal to close)
                    loadingDialog.close();
                    return; // Exit early to prevent running common close logic
                } else {
                    // No modals, navigate immediately
                    router.replace(route);
                }
            }

            // Common close logic for both edit and create (only runs if no modals)
            loadingDialog.close();
            if (id) {
                if (onClose) {
                    onClose();
                } else {
                    editPostDialog.close();
                }
            } else {
                alertDialog.close(false);
                setDone(true); // Only set done for create mode
            }
        } catch (error: any) {
            loadingDialog.close();
            // if (id) editPostDialog.close();
            console.error('[handleWritingPost] Catch block exception:', error);
            if (error?.response) {
                console.error('[handleWritingPost] Server Response details:', {
                    status: error.response.status,
                    data: error.response.data,
                    headers: error.response.headers
                });
            }
            let errorMessage = utilsService.getErrorMessage(error);
            console.log('[handleWritingPost] Extracted Error Message:', errorMessage);
            // Convert tags validation error to Korean
            if (errorMessage && /The tags\.\d+ field must not be greater than 50 characters/.test(errorMessage)) {
                errorMessage = t('tag_max_length_error', '태그는 50자 이하로 입력해주세요.');
            }

            addSnackbar({
                message: errorMessage || 'Some thing went wrong',
                severity: 'error',
                autoHideDuration: 3000,
                vertical: 'bottom',
                horizontal: 'center',
                type: 'toast',
            });
            setLoadingSubmit(false);
        }
    };

    const renderStepComponent = () => {
        switch (step) {
            case WritingStep.NEW_WRITING_FORM:
                return (
                    <NewWritingForm
                        ref={newWritingFormRef}
                        groups={group}
                        isEditMode={!!id}
                        initialData={{
                            caption: state.caption,
                            media: state.media,
                            groupId: state.groupId as string,
                            url: url,
                            mediaType: radioValue, // Always use current radioValue, not dependent on url existence
                            tags: tempTags,
                            boardType: tempBoardType,
                            price: tempPrice,
                            productName: tempProductName,
                        }}
                        onSubmit={(data) => {
                            // Determine mediaType và media dựa trên data.mediaType
                            let finalMedia = data.media;
                            let finalRadioValue = data.mediaType;

                            // Nếu là URL type, cần tạo URL media object
                            if (data.mediaType === 'URL' && url) {
                                finalRadioValue = 'URL';
                                finalMedia = [{
                                    mediaType: 'url',
                                    mediaUrl: url,
                                    order: 1,
                                } as any];
                            } else {
                                // Image type - filter ra chỉ image/video
                                finalRadioValue = 'image';
                                finalMedia = data.media.filter((item: any) =>
                                    item.mediaType === 'image' || item.mediaType === 'video'
                                );
                            }

                            // Update state với data mới
                            const newState = {
                                ...state,
                                caption: data.caption,
                                media: finalMedia,
                                groupId: data.groupId,
                            };
                            setState(newState);
                            setIsGroupSelected(data.groupId);
                            setRadioValue(finalRadioValue);

                            // Store tags, boardType, price, productName để dùng trong API call
                            setTempTags(data.tags);
                            setTempBoardType(data.boardType);
                            setTempPrice(data.price || '');
                            setTempProductName(data.productName || '');

                            // Gọi handleWritingPost sau khi state đã được set
                            setTimeout(() => {
                                handleWritingPost();
                            }, 50);
                        }}
                        onSwitchToUrl={() => {
                            // If URL already exists, just switch radioValue, don't open URL input modal
                            if (url) {
                                setRadioValue('URL');
                            } else {
                                setStep(WritingStep.WRITING_URL);
                            }
                        }}
                        onBackToUrlScreen={() => setStep(WritingStep.WRITING_URL)}
                        handleAvatarChange={handleAvatarChange}
                        removeImage={handleRemoveImage}
                        onCaptionChange={(caption) => setState((prev) => ({ ...prev, caption }))}
                        onDataChange={(data) => {
                            setState((prev) => ({ ...prev, caption: data.caption, groupId: data.groupId }));
                            setTempTags(data.tags);
                            setTempBoardType(data.boardType);
                            setTempPrice(data.price);
                            setTempProductName(data.productName);
                        }}
                        onMediaTypeChange={(mediaType) => {
                            setRadioValue(mediaType);
                        }}
                        onClearUrl={() => {
                            setUrl(null);
                            setRadioValue('image');

                            // Clear URL-type media from state
                            setState((prev) => ({
                                ...prev,
                                media: prev.media.filter((item) => {
                                    // Giữ lại image và video từ file upload, loại bỏ url type
                                    return 'mediaType' in item && (item.mediaType === 'image' || item.mediaType === 'video');
                                }),
                            }));
                        }}
                        handleDeleteUrl={handleDeleteUrl}
                    />
                );
            case WritingStep.CHOOSE_GROUP:
                return (
                    <ChooseGroup
                        dataGroup={group}
                        setIsGroupSelected={handleSetSelectGroup}
                        selected={isGroupSelected}
                    />
                );
            case WritingStep.CHOOSE_GROUP_WITHOUT_SELECT_IMG:
                return (
                    <ChooseGroup
                        dataGroup={group}
                        setIsGroupSelected={handleSetSelectGroup}
                        selected={isGroupSelected}
                    />
                );
            case WritingStep.WRITING_URL:
                return (
                    <UrlFormWriting
                        onListenUrlsChange={onUpdateUrlAndStatusUrl}
                        defaultUrl={url as string}
                        ref={urlFormRef}
                    />
                );
            case WritingStep.WRITING_POST:
                return (
                    <Writing
                        handleChangeCaption={onChangeCaption}
                        handleAvatarChange={handleAvatarChange}
                        state={state as CreatePostRequest | EditPostRequest}
                        status={id ? 'update' : 'create'}
                        onBackToUrlScreen={handleBackToUrlScreen}
                        onDeleteUrl={handleDeleteUrl}
                        fileAttachment={fileAttachment}
                        url={url}
                        optionWriting={radioValue}
                        showGroupArea={true} //is always show even no groups
                        group={isGroupSelected === 'all' ? 'all' : group.find((item) => item.id == isGroupSelected) || ''}
                        onChooseGroupAgain={handleBackToChooseGroup}
                        onFile={handleChangeFileAttach}
                        removeImage={handleRemoveImage}
                    />
                );
            default:
                return null;
        }
    };

    const getModalAcceptText = useCallback((step: WritingStep | null) => {
        switch (step) {
            case WritingStep.WRITING_POST:
                return t('yes', '예');
            default:
                return t('confirm', '확인');
        }
    }, [t]);

    const handleButtonClick = async () => {
        if (urlFormRef.current) {
            if (!url) return;
            await urlFormRef.current.handleCheckUrl();
            const checkUrlYoutube = utilsService.detectYouTubeUrl(url);
            if (!checkUrlYoutube) return;
            updateUrl();
            // Quay lại NEW_WRITING_FORM thay vì WRITING_POST
            setRadioValue('URL');
            setStep(WritingStep.NEW_WRITING_FORM);
        }
    };

    const renderHeader = useCallback((key: string) => {
        switch (key) {
            case WritingStep.NEW_WRITING_FORM:
                return t('writing', '글쓰기');
            case WritingStep.CHOOSE_GROUP:
                return t('select_group', '그룹 선택');
            case WritingStep.CHOOSE_GROUP_WITHOUT_SELECT_IMG:
                return t('select_group', '그룹 선택');
            case WritingStep.WRITING_POST:
                return t('writing', '글쓰기');
            case WritingStep.WRITING_URL:
                return t('video_url_input', '동영상 URL 입력');
            default:
                return t('select_group', '그룹 선택');
        }
    }, [t]);

    const renderActionClick = (step: WritingStep) => {
        switch (step) {
            case WritingStep.NEW_WRITING_FORM:
                return () => newWritingFormRef.current?.handleSubmit();
            case WritingStep.WRITING_POST:
                return () => handleWritingPost();
            case WritingStep.WRITING_URL:
                return async () => await handleButtonClick();
            default:
                return () => { };
        }
    };

    const renderRightElement = useCallback(() => {
        // Calculate canSubmit directly in parent based on current state
        // Check media based on current radioValue
        let hasMedia = false;
        if (radioValue === 'URL') {
            // For URL type, must have url
            hasMedia = !!url;
        } else {
            // For image type, must have image/video media in state (not url type)
            const imageVideoMedia = state?.media?.filter((item: any) => {
                if ('isDeleted' in item && item.isDeleted) return false;
                return item.mediaType === 'image' || item.mediaType === 'video';
            }) || [];
            hasMedia = imageVideoMedia.length > 0;
        }
        const hasCaption = !!state?.caption?.trim();
        const hasTags = tempTags && tempTags.length > 0;

        let canSubmit = false;
        if (WritingStep.NEW_WRITING_FORM.includes(step as WritingStep)) {
            if (tempBoardType === 'market') {
                // Market: caption + tags + media + productName + price (all required)
                canSubmit = hasCaption && hasTags && hasMedia && !!tempProductName?.trim() && !!tempPrice?.trim();
            } else {
                // Board: caption + tags + media (all required)
                canSubmit = hasCaption && hasTags && hasMedia;
            }
        }

        return [WritingStep.CHOOSE_GROUP, WritingStep.CHOOSE_GROUP_WITHOUT_SELECT_IMG].includes(step as WritingStep) ? null : (
            <>
                {WritingStep.NEW_WRITING_FORM.includes(step as WritingStep) ? (
                    <Button
                        sx={{ border: 'none', minWidth: 'auto' }}
                        disabled={!canSubmit}
                    >
                        <Typography variant='h2_14Medium' lineHeight="100%" sx={{ color: !canSubmit ? 'grayscale.gray04' : 'primary.main' }}>{t('complete', '완료')}</Typography>
                    </Button>
                ) : WritingStep.WRITING_URL.includes(step as WritingStep) ? (
                    <Button
                        sx={{ border: 'none', minWidth: 'auto' }}
                        disabled={!url || ((url !== null || url !== '') && (url?.length ?? 0) < 5) || isWrongUrl}
                    >
                        <Typography variant='h2_14Medium' lineHeight="100%" sx={{ color: !url || ((url !== null || url !== '') && (url?.length ?? 0) < 5) || isWrongUrl ? 'grayscale.gray04' : 'primary.main' }}>{t('attach', '첨부')}</Typography>
                    </Button>
                ) : (
                    <>
                        {radioValue === 'URL' ? (
                            <Button
                                sx={{ border: 'none', minWidth: 'auto' }}
                                disabled={!url || ((url !== null || url !== '') && (url?.length ?? 0) < 5) || isWrongUrl}
                            >
                                <Typography sx={{ color: !url || ((url !== null || url !== '') && (url?.length ?? 0) < 5) || isWrongUrl ? 'grayscale.gray04' : 'primary.main' }}>{t('complete', '완료')}</Typography>
                            </Button>
                        ) : (
                            <Button
                                sx={{ border: 'none', minWidth: 'auto' }}
                                disabled={!state.caption?.trim() || !tempTags?.length || !state.media?.length || done}
                            >
                                <Typography sx={{ color: (!state.caption?.trim() || !tempTags?.length || !state.media?.length || done) ? 'grayscale.gray04' : 'primary.main' }}>{t('complete', '완료')}</Typography>
                            </Button>
                        )}
                    </>
                )}
            </>
        );
    }, [step, loadingSubmit, state?.media, state?.caption, url, isWrongUrl, radioValue, handleWritingPost, newWritingFormRef, fileAttachment, done, tempTags, tempBoardType, tempPrice, tempProductName, t]);

    return (
        <>
            <FixedHeader
                text={step ? renderHeader(step) : ''}
                icon={[WritingStep.CHOOSE_GROUP_WITHOUT_SELECT_IMG].includes(step as WritingStep) ? <BackIcon /> : <CloseIcon />}
                onRightElementClick={renderActionClick(step as WritingStep)}
                rightElement={renderRightElement()}
                onClick={handleConfirmClose}
            />
            <Box sx={{ pt: '48px', height: '100%', overflow: 'auto', overflowX: 'hidden', backgroundColor: 'background.default' }}>{renderStepComponent()}</Box>
            <DrawerCustom
                open={openDrawer}
                onClose={() => setOpenDrawer(false)}
                title={t('sort', '정렬')}
            >
                <CenteredBox sx={{ flexDirection: 'column', paddingTop: 5, gap: 4, mb: 7.5, justifyContent: 'start' }}>
                    <CenteredBox sx={{ width: '100%', alignItems: 'start', justifyContent: 'start', px: 5 }}>
                        <RadioGroup
                            sx={{ display: 'flex', gap: 4, flexDirection: 'column' }}
                            value={radioValue}
                            onChange={handleChangeTypeRadio}
                        >
                            <FormControlLabel
                                sx={{ gap: 2.5, ml: 0 }}
                                value="image"
                                control={
                                    <Radio
                                        sx={{ padding: 0 }}
                                        value="image"
                                        onClick={() => handleWritingWithoutImg()}
                                        icon={<UnCheckedRadioButton />}
                                        checkedIcon={<CheckedRadioButton />}
                                    />
                                }
                                label={<Typography variant="h2_14Medium">{t('select_photos_videos', '갤러리에서 사진/영상 선택')}</Typography>}
                            />
                            {/* <FormControlLabel
                        value="3D"
                        control={
                            <Radio
                                icon={<UnCheckedRadioButton />}
                                checkedIcon={<CheckedRadioButton />}
                            />
                        }
                        label="영상을 3D로 변환"
                    /> */}
                            <FormControlLabel
                                onChange={() => setStep(WritingStep.WRITING_URL)}
                                value="URL"
                                control={
                                    <Radio
                                        icon={<UnCheckedRadioButton />}
                                        checkedIcon={<CheckedRadioButton />}
                                    />
                                }
                                label={t('register_video_by_url', 'URL로 동영상 등록')}
                            />
                        </RadioGroup>
                    </CenteredBox>
                </CenteredBox>
            </DrawerCustom>
            {showAlert && (
                <BaseModal
                    lineHeight="normal"
                    open={showAlert}
                    onClose={() => setShowAlertPopup(false)}
                    title={modalContent.title}
                    description={modalContent.description}
                    showAccept
                    showCancel={[WritingStep.WRITING_POST, WritingStep.WRITING_URL, WritingStep.CHOOSE_GROUP_WITHOUT_SELECT_IMG, WritingStep.NEW_WRITING_FORM].includes(step as WritingStep)}
                    onCancel={() => handlePopup(step as WritingStep, 'continue')}
                    onAccept={() => handlePopup(step as WritingStep, 'cancel_writing')}
                    acceptText={getModalAcceptText(step)}
                    cancelText={t('no', '아니오')}
                />
            )}
            {showAlertForSubmitAction && (
                <BaseModal
                    lineHeight="normal"
                    open={showAlertForSubmitAction}
                    onClose={() => setShowAlertForSubmitAction(false)}
                    title={modalContent.title}
                    description={modalContent.description}
                    showAccept
                    showCancel={false}
                    onCancel={() => setShowAlertForSubmitAction(false)}
                    onAccept={() => setShowAlertForSubmitAction(false)}
                    acceptText={t('confirm', '확인')}
                    cancelText={t('no', '아니오')}
                />
            )}
            <LevelUpModal
                open={showLevelUpModal}
                onClose={() => {
                    setShowLevelUpModal(false);
                    // Navigate after level up modal closes
                    if (pendingNavigation) {
                        router.replace(pendingNavigation.route);
                        setPendingNavigation(null);
                    }
                    // Run common close logic after navigation
                    if (id) {
                        if (onClose) {
                            onClose();
                        } else {
                            editPostDialog.close();
                        }
                    } else {
                        alertDialog.close(false);
                        setDone(true);
                    }
                }}
                levelUpData={levelUpData}
            />
            {showBonusModal && (
                <BaseModal
                    open={showBonusModal}
                    onClose={() => {
                        setShowBonusModal(false);
                        // Show level up modal after bonus modal closes if available
                        if (levelUpData) {
                            setTimeout(() => {
                                setShowLevelUpModal(true);
                            }, 0);
                        } else if (pendingNavigation) {
                            // No level up modal, navigate immediately
                            router.replace(pendingNavigation.route);
                            setPendingNavigation(null);
                            // Run common close logic after navigation
                            if (id) {
                                if (onClose) {
                                    onClose();
                                } else {
                                    editPostDialog.close();
                                }
                            } else {
                                alertDialog.close(false);
                                setDone(true);
                            }
                        }
                    }}
                    title={t('bonus_points_earned_title', `${bonusPoints.toLocaleString()} 포인트가 적립되었습니다.`).replace('{points}', bonusPoints.toLocaleString())}
                    description={''}
                    showCancel={false}
                    showAccept={true}
                    onCancel={() => {
                        setShowBonusModal(false);
                        // Show level up modal after bonus modal closes if available
                        if (levelUpData) {
                            setTimeout(() => {
                                setShowLevelUpModal(true);
                            }, 0);
                        } else if (pendingNavigation) {
                            // No level up modal, navigate immediately
                            router.replace(pendingNavigation.route);
                            setPendingNavigation(null);
                            // Run common close logic after navigation
                            if (id) {
                                if (onClose) {
                                    onClose();
                                } else {
                                    editPostDialog.close();
                                }
                            } else {
                                alertDialog.close(false);
                                setDone(true);
                            }
                        }
                    }}
                    onAccept={() => {
                        setShowBonusModal(false);
                        // Show level up modal after bonus modal closes if available
                        if (levelUpData) {
                            setTimeout(() => {
                                setShowLevelUpModal(true);
                            }, 0);
                        } else if (pendingNavigation) {
                            // No level up modal, navigate immediately
                            router.replace(pendingNavigation.route);
                            setPendingNavigation(null);
                            // Run common close logic after navigation
                            if (id) {
                                if (onClose) {
                                    onClose();
                                } else {
                                    editPostDialog.close();
                                }
                            } else {
                                alertDialog.close(false);
                                setDone(true);
                            }
                        }
                    }}
                    acceptText={t('confirm', '확인')}
                />
            )}
        </>
    );
};

export default WritingComponent;
